import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, FileText } from 'lucide-react';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-3xl">
              <FileText className="w-8 h-8 text-emerald-600" />
              Terms of Service
            </CardTitle>
            <p className="text-sm text-muted-foreground">Last Updated: February 17, 2026</p>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using SolelyTax AI ("the Service"), you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed">
                SolelyTax AI provides tax management software for self-employed individuals, freelancers, and small 
                business owners. The Service includes transaction tracking, expense categorization, tax estimation, 
                and report generation.
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Important:</strong> SolelyTax AI is a software tool that assists with tax preparation. It is NOT 
                a substitute for professional tax advice. The Service does not provide legal, tax, or accounting advice. 
                Users are responsible for the accuracy of their tax filings and should consult with licensed tax professionals.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
              <p className="text-gray-700 leading-relaxed">
                You are responsible for:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Ensuring the accuracy of information you provide</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">4. Subscription and Payment</h2>
              <p className="text-gray-700 leading-relaxed">
                SolelyTax Pro subscription is billed monthly at $19/month via Stripe. You may cancel at any time. 
                Cancellation will take effect at the end of your current billing period. No refunds for partial months.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">5. Data and Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                We take your privacy seriously. Your financial data is encrypted and stored securely. We will never sell 
                your personal information. See our Privacy Policy for detailed information about data collection and use.
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Bank Connections:</strong> When you connect your bank account via Stripe Financial Connections, 
                we access read-only transaction data. We cannot initiate transfers or access your funds.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">6. AI-Generated Content</h2>
              <p className="text-gray-700 leading-relaxed">
                Our AI advisors and categorization features use artificial intelligence (Google Gemini). While we strive 
                for accuracy, AI-generated content may contain errors. Users should:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Review all AI-categorized transactions before finalizing</li>
                <li>Verify AI advisor recommendations with tax professionals</li>
                <li>Not rely solely on AI advice for tax filing decisions</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">7. Disclaimer of Warranties</h2>
              <p className="text-gray-700 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. We do not guarantee:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Accuracy of tax calculations (for informational purposes only)</li>
                <li>Compliance with all federal, state, or local tax laws</li>
                <li>Uninterrupted or error-free service</li>
                <li>That the Service will meet your specific requirements</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">8. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                To the maximum extent permitted by law, SolelyTax AI shall not be liable for any indirect, incidental, 
                special, or consequential damages arising from your use of the Service, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Tax penalties or interest from IRS or state authorities</li>
                <li>Financial losses from incorrect categorizations</li>
                <li>Audit-related costs or consequences</li>
                <li>Lost profits or business opportunities</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">9. User Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Use the Service only for lawful purposes</li>
                <li>Provide accurate information</li>
                <li>Review and verify all calculations before tax filing</li>
                <li>Consult with qualified tax professionals when necessary</li>
                <li>Maintain records as required by tax authorities</li>
                <li>Not share your account with others</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">10. Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to suspend or terminate your account for violations of these Terms or for any reason 
                at our discretion. Upon termination, you may request a data export within 30 days.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">11. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may modify these Terms at any time. Continued use of the Service after changes constitutes acceptance 
                of the modified Terms. We will notify users of material changes via email.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">12. Contact</h2>
              <p className="text-gray-700 leading-relaxed">
                For questions about these Terms, contact us at: <strong>support@solelytax.ai</strong>
              </p>
            </section>

            <div className="mt-8 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>Questions?</strong> Visit our <a href="/help" className="text-emerald-600 hover:underline">Help Center</a> or contact support.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms;